import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, CloudUpload as CloudUploadIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import listaPrecioService, { ListaPrecioPagedResult } from '../../services/listaPrecioService';
import InformesBackButton from '../../components/shared/InformesBackButton';
import * as XLSX from 'xlsx';

const ListaPrecios: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [listasData, setListasData] = useState<ListaPrecioPagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [_loading, setLoading] = useState(false);

  const fetchListaPrecios = async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      setLoading(true);
      const response = await listaPrecioService.getListaPrecios({
        pageNumber,
        pageSize,
        searchTerm: search || undefined
      });
      setListasData(response);
    } catch (error: any) {
      console.error('Error al cargar listas de precios:', error);
      enqueueSnackbar('Error al cargar listas de precios', { variant: 'error' });
      setListasData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchListaPrecios(page + 1, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (codigoLista: string) => {
    try {
      const check = await listaPrecioService.puedeEliminar(codigoLista);
      if (!check.canDelete) {
        enqueueSnackbar(check.message || check.motivos?.[0] || 'No se puede eliminar la lista de precios.', { variant: 'error' });
        return;
      }
      if (!window.confirm('¿Está seguro de eliminar esta lista de precios?')) return;
      await listaPrecioService.deleteListaPrecio(codigoLista);
      enqueueSnackbar('Lista de precios eliminada exitosamente', { variant: 'success' });
      fetchListaPrecios(page + 1, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      console.error('Error al eliminar lista de precios:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      enqueueSnackbar(axiosError?.response?.data?.message || 'Error al eliminar lista de precios', { variant: 'error' });
    }
  };

  const handleExportExcel = () => {
    const dataToExport = listasData.items.map(l => ({
      'Código': l.codigoLista,
      'Descripción': l.descripcion,
      'Proveedor': l.nroProveedor,
      'Moneda': l.codigoMoneda,
      '% Ganancia': l.porcGan,
      'Fecha Creación': l.fechaCreacion
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Listas de Precios');
    XLSX.writeFile(wb, 'Listas_Precios.xlsx');
    enqueueSnackbar('Archivo exportado exitosamente', { variant: 'success' });
  };

  const handleImportExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        let successCount = 0;
        let errorCount = 0;

        for (const row of jsonData as any[]) {
          try {
            const listaPrecio = {
              codigoLista: row['Código'] || row['codigoLista'],
              descripcion: row['Descripción'] || row['descripcion'],
              cp: row['CP'] || row['cp'] || '',
              nroProveedor: Number(row['Proveedor'] || row['nroProveedor']),
              fechaCreacion: row['Fecha Creación'] || row['fechaCreacion'] || new Date().toISOString(),
              codigoMoneda: row['Moneda'] || row['codigoMoneda'] || '1',
              porcGan: Number(row['% Ganancia'] || row['porcGan'] || 0)
            };

            await listaPrecioService.createListaPrecio(listaPrecio);
            successCount++;
          } catch (error) {
            errorCount++;
            console.error('Error al importar lista de precios:', error);
          }
        }

        enqueueSnackbar(`Importación completada: ${successCount} exitosos, ${errorCount} errores`, { 
          variant: errorCount > 0 ? 'warning' : 'success' 
        });
        fetchListaPrecios(page + 1, rowsPerPage, searchTerm);
      } catch (error) {
        enqueueSnackbar('Error al procesar el archivo Excel', { variant: 'error' });
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <InformesBackButton to="/inventario" useBrowserBack />
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Listas de Precios
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="import-excel-listaprecios"
                type="file"
                onChange={handleImportExcel}
              />
              <label htmlFor="import-excel-listaprecios">
                <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="medium">
                  Importar
                </Button>
              </label>
              <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={handleExportExcel} size="medium">
                Exportar
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                size="medium"
                onClick={() => navigate('/ventas/listaprecios/nuevo')}
              >
                Nueva Lista
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {listasData.items.map((lista) => (
                <Card key={lista.codigoLista} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {lista.codigoLista} - {lista.descripcion}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ganancia: {lista.porcGan}%
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/ventas/listaprecios/${lista.codigoLista}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(lista.codigoLista)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Moneda</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>% Ganancia</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listasData.items.map((lista) => (
                    <TableRow key={lista.codigoLista} hover>
                      <TableCell>{lista.codigoLista}</TableCell>
                      <TableCell>{lista.descripcion}</TableCell>
                      <TableCell>{lista.nroProveedor}</TableCell>
                      <TableCell>{lista.codigoMoneda}</TableCell>
                      <TableCell>{lista.porcGan}%</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/ventas/listaprecios/${lista.codigoLista}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(lista.codigoLista)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={listasData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ListaPrecios;
